import { DEFAULT_LIMIT } from "@src/components/intelligence-search/template-search";
import { OPERATOR_LABEL_MAP } from "@src/pages/intelligence-search/constant/filter-operator";
import { OperatorEnum } from "@src/pages/intelligence-search/enum";
import { SmlAsset, SmlJsonQuery } from "@src/types/intelligence-search";

const directionMapLabel = {
    asc: "ASC",
    desc: "DESC",
};

export class SpecSectionsBuilder {
    private sections: any[] = [];

    constructor(private assetMeta: SmlAsset[], private query: SmlJsonQuery, private limit?: number) {}
    private getLimit() {
        return this.limit ?? this.query.limit ?? DEFAULT_LIMIT;
    }
    private createMetricPhrases() {
        const _querySelect = (this.query.select ?? []).map((s) => (typeof s === "string" ? s : s.alias));
        const phrases = [
            { type: "text", value: "Viewing" },
            { type: "text", value: " " },
        ];

        phrases.push(
            ...this.assetMeta
                .filter(({ asset_type }) => asset_type === "metric")
                .filter(({ name }) => _querySelect.find((s) => name === s))
                .map((meta, idx) => {
                    const rs = [];
                    if (idx > 0) {
                        rs.push({ type: "text", value: ", " });
                    }
                    rs.push({
                        type: "custom",
                        value: meta.title || meta.name,
                        metadata: {
                            customType: "ama-sml-asset",
                            assetMeta: meta,
                        },
                    });
                    return rs;
                })
                .flat(),
        );

        return phrases;
    }

    private createDimensionPhrases() {
        const _querySelect = (this.query.select ?? []).map((s) => (typeof s === "string" ? s : s.alias));
        const dimensionPhrases: any[] = [];

        if (
            _querySelect.filter((s) => {
                return this.assetMeta.find(({ asset_type, name }) => asset_type === "dimension" && name === s);
            }).length > 0
        ) {
            dimensionPhrases.push({ type: "text", value: "; grouped by" }, { type: "text", value: " " });

            dimensionPhrases.push(
                ...this.assetMeta
                    .filter(({ asset_type }) => asset_type === "dimension")
                    .filter(({ name }) => _querySelect.find((s) => name === s))
                    .map((meta, idx) => {
                        const rs = [];
                        if (idx > 0) {
                            rs.push({ type: "text", value: ", " });
                        }
                        rs.push({
                            type: "custom",
                            value: meta.title || meta.name,
                            metadata: {
                                customType: "ama-sml-asset",
                                assetMeta: meta,
                            },
                        });
                        return rs;
                    })
                    .flat(),
            );
        }

        return dimensionPhrases;
    }

    private createFiltersParagraph() {
        if (!this.query.filters || this.query.filters.length === 0) {
            return null;
        }

        return {
            type: "bullets",
            isOrder: true,
            bullets: this.query.filters.map((f) => {
                const meta = this.assetMeta.find(({ name }) => name === f.name);
                const _phrases = [];

                if (meta !== undefined) {
                    _phrases.push(
                        {
                            type: "custom",
                            value: meta.title || meta.name,
                            metadata: {
                                customType: "ama-sml-asset",
                                assetMeta: meta,
                            },
                        },
                        { type: "text", value: " " },
                        { type: "text", value: OPERATOR_LABEL_MAP[f.operator as OperatorEnum] },
                        { type: "text", value: " " },
                        {
                            type: "text",
                            value: f.values === undefined ? "" : f.values.join(", "),
                            underline: true,
                            bold: true,
                        },
                    );
                }

                return {
                    type: "bullet-item",
                    phrases: _phrases,
                };
            }),
        };
    }

    private createLimitAndOrderPhrases() {
        const limitPhrases: any[] = [];
        const orderPhrases: any[] = [];

        if (this.getLimit()) {
            limitPhrases.push(
                { type: "text", value: "Showing top" },
                { type: "text", value: " " },
                { type: "text", value: `${this.getLimit()}`, bold: true, underline: true },
                { type: "text", value: " records" },
            );
        }

        if (this.query.orders && this.query.orders.length > 0) {
            orderPhrases.push({ type: "text", value: " ; Ordered by " });

            this.query.orders.forEach((_order, index) => {
                const meta = this.assetMeta.find(({ name }) => name === _order.name);
                if (meta) {
                    const isEnd = index === (this.query.orders?.length ?? 0) - 1;
                    orderPhrases.push(
                        {
                            type: "custom",
                            value: `${meta.title ?? meta.name}`,
                            metadata: {
                                customType: "ama-sml-asset",
                                assetMeta: meta,
                            },
                        },
                        { type: "text", value: ` (${directionMapLabel[_order.direction]}) `, bold: true },
                        { type: "text", value: `${isEnd ? "" : ", "}` },
                    );
                }
            });
        }

        return [...limitPhrases, ...orderPhrases];
    }

    build() {
        const metricPhrases = this.createMetricPhrases();
        const dimensionPhrases = this.createDimensionPhrases();
        const filtersParagraph = this.createFiltersParagraph();
        const limitAndOrderPhrases = this.createLimitAndOrderPhrases();

        // Add filter intro phrases if filters exist
        const mainPhrases = [...metricPhrases, ...dimensionPhrases];
        if (this.query.filters && this.query.filters.length > 0) {
            mainPhrases.push({ type: "text", value: "; for" }, { type: "text", value: " " });
        }

        const paragraphs: any[] = [
            {
                type: "normal",
                phrases: mainPhrases,
            },
        ];

        // Add filters paragraph if it exists
        if (filtersParagraph) {
            paragraphs.push(filtersParagraph);
        }

        // Add limit and order paragraph if it has content
        if (limitAndOrderPhrases.length > 0) {
            paragraphs.push({
                type: "normal",
                phrases: limitAndOrderPhrases,
            });
        }

        return {
            sections: [
                {
                    customType: "section",
                    paragraphs,
                },
            ],
        } as any;
    }
}
